import PropTypes from 'prop-types';
import React from 'react';
import { get } from 'lodash';
import { ListComponent, VerticalSpacing } from '@custom-components';
import { NUM_PER_PAGE } from './helperFunctions';

class PaginatedList extends React.Component {
  static propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    loading: PropTypes.bool,
    error: PropTypes.shape({
      message: PropTypes.string
    }),
    viewFilters: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number
      })
    ),
    sortFilters: PropTypes.arrayOf(PropTypes.shape({})),
    initialViewFilterId: PropTypes.string,
    initialSortFilterId: PropTypes.string,
    initialQueryValue: PropTypes.string,
    initialToggleValue: PropTypes.string,
    initialPaginationValue: PropTypes.shape({}),
    screenClass: PropTypes.string.isRequired,
    tableComponent: PropTypes.shape({}).isRequired,
    cardComponent: PropTypes.func,
    helpText: PropTypes.string,
    noDataObj: PropTypes.shape({ message: PropTypes.string, title: PropTypes.string }),
    searchBy: PropTypes.arrayOf(PropTypes.string),
    listComponentProps: PropTypes.shape({}),
    otherComponentProps: PropTypes.shape({}),
    headerComponentProps: PropTypes.shape({}),
    funcComponentProps: PropTypes.shape({}),
    paginationFunctionsProps: PropTypes.shape({
      handleChangePageSize: PropTypes.func,
      handleChangePage: PropTypes.func
    }),
    onSearchChange: PropTypes.func,
    onFilterChange: PropTypes.func,
    onSortChange: PropTypes.func,
    header: PropTypes.bool,
    pagination: PropTypes.bool,
    totalItems: PropTypes.number.isRequired
  };

  static defaultProps = {
    loading: true,
    error: null,
    viewFilters: [],
    sortFilters: [],
    initialSortFilterId: undefined,
    initialViewFilterId: undefined,
    initialQueryValue: undefined,
    initialToggleValue: undefined,
    initialPaginationValue: {},
    helpText: '',
    noDataObj: { message: '', title: 'No Data' },
    searchBy: [],
    cardComponent: undefined,
    listComponentProps: {},
    otherComponentProps: {},
    headerComponentProps: {},
    onSearchChange: () => {},
    onFilterChange: () => {},
    onSortChange: () => {},
    header: true,
    pagination: true,
    funcComponentProps: () => {},
    paginationFunctionsProps: {
      handleChangePageSize: () => {},
      handleChangePage: () => {},
      handleChangeSort: () => {},
      handleChangeFilter: () => {},
      handleChangeSearchString: () => {}
    }
  };

  constructor(props) {
    super(props);

    this.state = {
      currentPage: get(props, 'initialPaginationValue.currentPage', 1),
      perPageIndex: get(props, 'initialPaginationValue.perPageIndex', 0)
    };

    this.firstPage = this.firstPage.bind(this);
    this.nextPage = this.nextPage.bind(this);
    this.prevPage = this.prevPage.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleChangePage = props.paginationFunctionsProps.handleChangePage.bind(this);
    this.handleChangePageSize = props.paginationFunctionsProps.handleChangePageSize.bind(this);
  }

  componentDidUpdate(nextProps) {
    const { initialQueryValue } = this.props;

    if (nextProps.initialQueryValue !== initialQueryValue) {
      // Return to page 1 when search is changed
      this.firstPage();
    }
  }

  getPerPage(index) {
    const { screenClass } = this.props;

    if (index < 0) return undefined;

    return NUM_PER_PAGE[screenClass][index];
  }

  /**
   * Pagination Controls.
   *
   * Sets state: currentPage.
   */
  nextPage(total) {
    const { currentPage, perPageIndex } = this.state;
    const totalPages = Math.ceil(total / this.getPerPage(perPageIndex));
    const nextPage = currentPage + 1 <= totalPages ? currentPage + 1 : currentPage;
    const update = {
      currentPage: nextPage
    };
    this.handleChangePage(nextPage);
    this.setState(update);
  }

  /**
   * Pagination Controls.
   *
   * Sets state: currentPage.
   */
  prevPage() {
    const { currentPage } = this.state;
    const prevPage = currentPage - 1 >= 1 ? currentPage - 1 : currentPage;
    const update = {
      currentPage: prevPage
    };
    this.handleChangePage(prevPage);
    this.setState(update);
  }

  /**
   * Jump to the first page
   */
  firstPage() {
    const update = { currentPage: 1 };
    this.handleChangePage(1);
    this.setState(update);
  }

  /**
   * Sets items per page
   *
   * Sets state: perPage.
   */
  handleSelect(e) {
    const { screenClass } = this.props;
    const newSize = parseInt(e.target.value, 10);
    const perPageIndex = NUM_PER_PAGE[screenClass].indexOf(newSize);
    const change = {
      perPageIndex,
      currentPage: 1
    };
    this.handleChangePageSize(newSize);
    this.setState(change);
  }

  render() {
    const {
      data,
      loading,
      error,
      viewFilters,
      sortFilters,
      onSearchChange,
      onFilterChange,
      onSortChange,
      initialViewFilterId,
      initialSortFilterId,
      initialQueryValue,
      initialToggleValue,
      tableComponent,
      cardComponent,
      helpText,
      noDataObj,
      searchBy,
      listComponentProps,
      otherComponentProps,
      headerComponentProps,
      funcComponentProps,
      header,
      pagination,
      totalItems
    } = this.props;

    const { currentPage, perPageIndex } = this.state;

    return (
      <ListComponent
        data={data}
        searchBy={searchBy}
        useFilters={{
          sort: sortFilters,
          view: viewFilters
        }}
        interactionHooks={{
          onSearchChange: (searchStr) => {
            this.firstPage();
            onSearchChange(searchStr);
          },
          onViewFilterClick: onFilterChange,
          onSortFilterClick: onSortChange
        }}
        initialViewFilterId={initialViewFilterId || viewFilters[0].id}
        initialSortFilterId={initialSortFilterId}
        initialQueryValue={initialQueryValue}
        defaultView={initialToggleValue}
        page={currentPage}
        perPage={this.getPerPage(perPageIndex)}
        {...listComponentProps}
      >
        {({ currentView, screenClass, constants, headerProps, paginationMessages, paginationProps }) => {
          const Component = currentView === constants.CARD_VIEW ? cardComponent : tableComponent;
          let props = { data, ...otherComponentProps };
          const noData = !Array.isArray(data) || !data.length;
          const headerInfo = { ...headerProps, helpText, ...headerComponentProps };

          if (!noData) {
            props = { ...props, funcComponentProps };
          }
          if (loading)
            return (
              <>
                {header && <ListComponent.Header {...headerInfo} />}
                <ListComponent.Loading />
              </>
            );
          if (error)
            return (
              <>
                {header && <ListComponent.Header {...headerInfo} />}
                <Component {...props} screenClass={screenClass} />

                <VerticalSpacing>
                  <p>{`Error! ${error.message}`}</p>
                </VerticalSpacing>
              </>
            );
          return (
            <>
              {header && <ListComponent.Header {...headerInfo} />}
              {noData ? (
                <ListComponent.NoData
                  actionLabel={null}
                  icon="facilities"
                  message={noDataObj.message}
                  title={noDataObj.title}
                />
              ) : (
                <Component {...props} screenClass={screenClass} />
              )}
              {pagination && (
                <ListComponent.Pagination
                  {...paginationProps}
                  showAll={false}
                  itemTotal={totalItems}
                  prevPage={this.prevPage}
                  nextPage={() => this.nextPage(totalItems)}
                  selectOptions={NUM_PER_PAGE[screenClass]}
                  selectFunc={this.handleSelect}
                  messages={paginationMessages}
                />
              )}
            </>
          );
        }}
      </ListComponent>
    );
  }
}

export default PaginatedList;
